import { useCallback, useRef } from 'react';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';
import { usePolling } from '../../hooks/usePolling';
import ActivityMonitor from '../../components/ActivityMonitor';
import UploadHistory from '../../components/UploadHistory';
import MetricsBar from '../../components/MetricsBar';
import PendingUpdatesCallout from '../../components/inventory/PendingUpdatesCallout';

/**
 * UploadTab — the canonical "what's processing right now" view.
 *
 * Renders:
 *   - MetricsBar:            status counts across all docs
 *   - PendingUpdatesCallout: stages that need attention but are NOT
 *                            already being applied (self-suppresses
 *                            while the coordinator's job is in flight)
 *   - ActivityMonitor:       uploaders + per-doc processing + indexing
 *                            + upgrading (tier upgrades / stage backfills
 *                            / transcription) — same /background-jobs
 *                            feed the inventory's BackgroundJobsBanner
 *                            uses, just rendered per-doc to match the
 *                            upload pipeline's UX.
 *   - UploadHistory:         completed / failed log with retry
 *
 * The InventoryTab keeps the BackgroundJobsBanner as a case-level
 * summary view. The two surfaces are complementary, not duplicated:
 * Upload & Processing = per-doc detail; Inventory = case-level rollup.
 */
export default function UploadTab({ caseId, onUploadStart: notifyParentStart, onUploadComplete: notifyParentComplete }) {
  const { documents, dispatch, caseData, caseDashboard } = useApp();
  const costLimitUsd = caseData?.settings?.upload_cost_limit_usd || null;

  // Delta-fetch cursor — same pattern as InventoryTab. CaseDetail keeps
  // UploadTab mounted on every tab (`display: none` when inactive) so
  // the in-memory upload queue survives switches; that also means
  // UploadTab's poll fires on every case page, regardless of which tab
  // the user is looking at. Without this cursor, UploadTab's 30s idle
  // poll re-downloaded the full ~3.74MB list every cycle even when the
  // user was on the Inventory tab.
  const lastDocsServerTimeRef = useRef(
    caseDashboard?.documents?.server_time || null,
  );

  const statusCounts = (documents || []).reduce((acc, d) => {
    // Effective status: surface 'empty' (outcome_status='empty' on a
    // completed doc) separately from 'completed' so the upload-tab
    // metric distinguishes "really failed" from "pipeline ran cleanly,
    // no extractable content."
    let s = d.status || 'unknown';
    if (s === 'completed' && d.outcome_status === 'empty') s = 'empty';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const hasProcessing = (statusCounts.processing || 0) + (statusCounts.pending || 0) > 0;
  // failedCount drives the "Retry N Failed" button — exclude empties so
  // the user isn't pushed to reprocess docs that already completed.
  const failedCount = statusCounts.failed || 0;

  const refreshDocs = useCallback(async (forceFull = false) => {
    try {
      const since = forceFull ? null : lastDocsServerTimeRef.current;
      const path = since
        ? `/documents/case/${caseId}?since=${encodeURIComponent(since)}`
        : `/documents/case/${caseId}`;
      const docsData = await api.get(path);
      lastDocsServerTimeRef.current =
        docsData?.server_time || new Date().toISOString();
      if (since && docsData?.since) {
        dispatch({ type: 'MERGE_DOCUMENTS', docs: docsData.documents || [] });
      } else {
        dispatch({ type: 'SET_DOCUMENTS', docs: docsData.documents || docsData });
      }
    } catch {
      // non-fatal
    }
  }, [caseId, dispatch]);

  // Poll documents: fast (5s) while processing, slow (30s) background poll so
  // status changes (retries, late completions) always reach the UI.
  usePolling(refreshDocs, hasProcessing ? 5000 : 30000);

  // Called by ActivityMonitor when an upload starts — immediate doc refresh +
  // notify CaseDetail so it can block page-close and show the badge.
  const handleUploadStart = useCallback(() => {
    setTimeout(refreshDocs, 2000); // slight delay for backend to register the upload
    notifyParentStart?.();
  }, [refreshDocs, notifyParentStart]);

  const handleUploadComplete = useCallback(() => {
    refreshDocs();
    notifyParentComplete?.();
  }, [refreshDocs, notifyParentComplete]);

  return (
    <div className="upload-tab">
      <MetricsBar caseId={caseId} />

      {/* Pending stage upgrades that are NOT already being applied.
          Self-suppresses while the coordinated backfill runs.
          BackgroundJobsBanner intentionally NOT rendered here — the
          ActivityMonitor below surfaces those same jobs per-doc. */}
      <PendingUpdatesCallout caseId={caseId} onDispatched={refreshDocs} />

      {/* ActivityMonitor owns the uploaders + all in-progress status */}
      <div className="upload-tab-section upload-tab-section-compact">
        <ActivityMonitor
          caseId={caseId}
          documents={documents}
          costLimitUsd={costLimitUsd}
          onUploadComplete={handleUploadComplete}
          onUploadStart={handleUploadStart}
        />
      </div>

      {documents?.length > 0 && (
        <div className="upload-tab-section">
          <UploadHistory
            documents={documents}
            failedCount={failedCount}
            caseId={caseId}
            onRetryComplete={refreshDocs}
          />
        </div>
      )}
    </div>
  );
}
